// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Monday Madness card in the Assistant DM. Edit the weekly report as a document,
// preview the exact email, and send it or copy it to Outlook Drafts. Edits save on their own.
// Dismiss skips this week or turns the Monday card off.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {MondayRecipients} from '@app/features/konvo_reports/components/MondayRecipients';
import {MondayReportCustomise} from '@app/features/konvo_reports/components/MondayReportCustomise';
import {MondayReportEditor} from '@app/features/konvo_reports/components/MondayReportEditor';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {KonvoIcon} from '@app/features/ui/components/icons/KonvoIcon';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {ActionButton, IconButton, Notice, Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {
	type MondayReportDraft,
	MondayReportDraftSchema,
	renderMondayDraft,
} from '@konvo/schema/src/domains/konvo/MondayReportDraft';
import {useLingui} from '@lingui/react/macro';
import {IconAdjustmentsHorizontal, IconDots, IconRefresh, IconSend, IconTemplate, IconX} from '@tabler/icons-react';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';
import styles from './MondayReportCard.module.css';

const ICON = {size: 18, stroke: 1.75} as const;
const AUTOSAVE_MS = 1200;

interface DraftResponse {
	draft: MondayReportDraft;
	revision: number;
	done: boolean;
	expired?: boolean;
	note?: string | null;
}

type PostAction = 'save' | 'send' | 'outlook' | 'layout' | 'forget_layout' | 'rebuild';
type Confirm = 'dismiss' | 'rebuild' | null;

export function MondayReportCard({cardId}: {cardId: string}) {
	const {t} = useLingui();
	const [draft, setDraft] = useState<MondayReportDraft | null>(null);
	const [saved, setSaved] = useState('');
	const [preview, setPreview] = useState(false);
	const [customise, setCustomise] = useState(false);
	const [confirm, setConfirm] = useState<Confirm>(null);
	const [done, setDone] = useState(false);
	const [expired, setExpired] = useState(false);
	const [busy, setBusy] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [conflict, setConflict] = useState(false);
	const [note, setNote] = useState<string | null>(null);
	const [attempt, setAttempt] = useState(0);
	const frame = useRef<HTMLIFrameElement>(null);
	const [frameHeight, setFrameHeight] = useState(500);
	// Saves run one after another so each carries the revision the previous one returned.
	const queue = useRef<Promise<unknown>>(Promise.resolve());
	const revision = useRef(0);
	const latest = useRef<MondayReportDraft | null>(null);
	latest.current = draft;
	const dirty = draft !== null && JSON.stringify(draft) !== saved;
	const locked = done || expired;
	const endpoint = Endpoints.KONVO_CARD_REPORT(cardId);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		setConflict(false);
		void http
			.get<DraftResponse>(endpoint, RETRY_RATE_LIMITS)
			.then((response) => {
				if (cancelled || !response.body) return;
				const data = response.body;
				setSaved(JSON.stringify(data.draft));
				setDraft(data.draft);
				revision.current = data.revision;
				setDone(data.done);
				setExpired(data.expired === true);
				setNote(data.note ?? null);
			})
			.catch(() => {
				if (!cancelled) setError(t`Could not load this draft. It may have expired.`);
			});
		return () => {
			cancelled = true;
		};
	}, [endpoint, attempt]);

	useEffect(() => {
		if (!dirty) return;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
		};
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [dirty]);

	const resizePreview = () => {
		const height = frame.current?.contentDocument?.body?.getBoundingClientRect().height;
		if (height) setFrameHeight(Math.max(400, height + 48));
	};
	useEffect(() => {
		if (!preview || !frame.current) return;
		const observer = new ResizeObserver(resizePreview);
		observer.observe(frame.current);
		return () => observer.disconnect();
	}, [preview]);

	const post = (action: PostAction) => {
		const run = async () => {
			const current = latest.current;
			if (!current || locked) return;
			const quiet = action === 'save';
			if (quiet) setSaving(true);
			else setBusy(true);
			setError(null);
			if (!quiet) setNote(null);
			try {
				const response = await http.post<DraftResponse>(endpoint, {
					body: {draft: current, revision: revision.current, action},
				});
				if (!response.body) throw new Error('No response');
				revision.current = response.body.revision;
				if (action === 'rebuild') {
					setDraft(response.body.draft);
					setSaved(JSON.stringify(response.body.draft));
				} else {
					// Keep what the user typed while this was in flight; the next autosave sends it.
					setSaved(JSON.stringify(current));
				}
				setDone(response.body.done);
				if (!quiet) setNote(response.body.note ?? (response.body.done ? t`Report sent.` : t`Saved.`));
			} catch (failure) {
				const status = (failure as {status?: number} | null)?.status;
				if (status === 409) setConflict(true);
				setError(
					status === 409
						? t`This report changed on another device. Reload to get the latest version; copy anything you need first.`
						: t`Could not save. Your edits are still here and save again as you keep typing.`,
				);
			} finally {
				if (quiet) setSaving(false);
				else setBusy(false);
			}
		};
		queue.current = queue.current.then(run, run);
		return queue.current;
	};

	useEffect(() => {
		if (!dirty || locked || busy || conflict || !draft) return;
		if (!MondayReportDraftSchema.safeParse(draft).success) return;
		const timer = window.setTimeout(() => void post('save'), AUTOSAVE_MS);
		return () => window.clearTimeout(timer);
	}, [draft, dirty, locked, busy, conflict]);

	const update = (change: Partial<MondayReportDraft>) =>
		setDraft((current) => (current ? {...current, ...change} : current));

	const deliver = (action: 'send' | 'outlook') => {
		if (!draft) return;
		if (!draft.recipients.length) {
			setError(t`Add at least one recipient first.`);
			return;
		}
		if (!MondayReportDraftSchema.safeParse(draft).success) {
			setError(t`Check the subject and links, and shorten any very long sections.`);
			return;
		}
		void post(action);
	};

	const dismiss = async (how: 'skip' | 'stop') => {
		setBusy(true);
		setError(null);
		try {
			if (how === 'stop') {
				// Load first: saving the calendar preferences sends every field.
				await KonvoCalendarPrefs.load();
				await KonvoCalendarPrefs.save({mondayReport: false});
			}
			await queue.current;
			await http.post(Endpoints.KONVO_CARD_ACTION(cardId, how), {body: {}});
			setDone(true);
			setConfirm(null);
			setNote(how === 'stop' ? t`Monday Madness is off.` : t`Skipped this week.`);
		} catch {
			setError(t`Could not dismiss the report. Try again.`);
		} finally {
			setBusy(false);
		}
	};

	const openMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
		ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => (
			<MenuGroup>
				<MenuItem
					icon={<OutlookLogo size={18} />}
					disabled={busy || locked}
					onClick={() => {
						onClose();
						deliver('outlook');
					}}
				>
					{t`Copy to Outlook Drafts`}
				</MenuItem>
				<MenuItem
					icon={<IconRefresh {...ICON} />}
					disabled={busy || locked}
					onClick={() => {
						onClose();
						setConfirm('rebuild');
					}}
				>
					{t`Rebuild from ClickUp`}
				</MenuItem>
				<MenuItem
					icon={<IconTemplate {...ICON} />}
					disabled={busy || locked}
					onClick={() => {
						onClose();
						void post('layout');
					}}
				>
					{t`Remember this layout`}
				</MenuItem>
			</MenuGroup>
		));

	if (!draft)
		return (
			<article className={styles.card} data-flx="konvo-monday.draft-card">
				<div className={styles.loading}>
					<p>{error || t`Preparing your weekly draft…`}</p>
					{error && (
						<ActionButton small onClick={() => setAttempt((value) => value + 1)}>
							{t`Try again`}
						</ActionButton>
					)}
				</div>
			</article>
		);

	const status = done ? t`Sent` : expired ? t`Expired` : saving ? t`Saving…` : dirty ? t`Edited` : t`Saved`;
	const recipientNames = draft.recipients.map((person) => person.name?.split(' ')[0] || person.address).join(', ');

	return (
		<article className={styles.card} data-flx="konvo-monday.draft-card">
			<header className={styles.header}>
				<div className={styles.identity}>
					<KonvoIcon width={18} height={18} />
					<strong>{t`Monday Madness`}</strong>
					<span className={styles.week}>{draft.weekLabel}</span>
					<span className={styles.status} data-state={dirty || saving ? 'pending' : 'saved'}>
						{status}
					</span>
				</div>
				<div className={styles.headerActions}>
					<Segmented<'edit' | 'preview'>
						compact
						value={preview ? 'preview' : 'edit'}
						onChange={(mode) => setPreview(mode === 'preview')}
						options={[
							{value: 'edit', children: t`Edit`},
							{value: 'preview', children: t`Preview`},
						]}
					/>
					<IconButton
						label={t`Customise`}
						icon={<IconAdjustmentsHorizontal {...ICON} />}
						active={customise}
						disabled={locked}
						onClick={() => setCustomise((open) => !open)}
					/>
					<IconButton label={t`More`} icon={<IconDots {...ICON} />} onClick={openMenu} />
					<IconButton
						label={t`Dismiss`}
						icon={<IconX {...ICON} />}
						disabled={busy || locked}
						onClick={() => setConfirm(confirm === 'dismiss' ? null : 'dismiss')}
					/>
				</div>
			</header>

			{confirm === 'dismiss' && (
				<Notice
					className={styles.confirm}
					action={
						<div className={styles.confirmActions}>
							<ActionButton small tone="quiet" onClick={() => setConfirm(null)}>
								{t`Cancel`}
							</ActionButton>
							<ActionButton small disabled={busy} onClick={() => void dismiss('stop')}>
								{t`Turn off every Monday`}
							</ActionButton>
							<ActionButton small tone="primary" disabled={busy} onClick={() => void dismiss('skip')}>
								{t`Skip this week`}
							</ActionButton>
						</div>
					}
				>
					{t`Dismiss this week’s report? Nothing is sent.`}
				</Notice>
			)}
			{confirm === 'rebuild' && (
				<Notice
					className={styles.confirm}
					tone="warning"
					action={
						<div className={styles.confirmActions}>
							<ActionButton small tone="quiet" onClick={() => setConfirm(null)}>
								{t`Cancel`}
							</ActionButton>
							<ActionButton
								small
								tone="primary"
								disabled={busy}
								onClick={() => {
									setConfirm(null);
									void post('rebuild');
								}}
							>
								{t`Rebuild`}
							</ActionButton>
						</div>
					}
				>
					{t`Replace the projects and sections with fresh ClickUp data? Your subject, heading, intro and sign-off stay.`}
				</Notice>
			)}
			{error && (
				<Notice
					className={styles.confirm}
					tone="danger"
					action={
						conflict ? (
							<ActionButton small onClick={() => setAttempt((value) => value + 1)}>
								{t`Reload`}
							</ActionButton>
						) : undefined
					}
				>
					{error}
				</Notice>
			)}
			{note && !error && <Notice className={styles.confirm}>{note}</Notice>}
			{expired && (
				<Notice
					className={styles.confirm}
				>{t`This draft has expired. Ask the assistant for a new Monday Madness card.`}</Notice>
			)}

			{customise && !locked && (
				<MondayReportCustomise
					draft={draft}
					update={update}
					busy={busy}
					onSaveLayout={() => void post('layout')}
					onForgetLayout={() => void post('forget_layout')}
				/>
			)}

			<fieldset className={styles.envelope} disabled={busy || locked}>
				<div className={styles.envelopeRow}>
					<span className={styles.envelopeLabel}>{t`To`}</span>
					<MondayRecipients recipients={draft.recipients} onChange={(recipients) => update({recipients})} />
				</div>
				<label className={styles.envelopeRow}>
					<span className={styles.envelopeLabel}>{t`Subject`}</span>
					<input
						className={styles.subject}
						value={draft.subject}
						maxLength={255}
						onChange={(event) => update({subject: event.target.value})}
					/>
				</label>
			</fieldset>

			{preview ? (
				<iframe
					ref={frame}
					className={styles.preview}
					style={{height: frameHeight}}
					title={t`Email preview`}
					sandbox="allow-same-origin"
					srcDoc={renderMondayDraft(draft)}
					onLoad={resizePreview}
				/>
			) : (
				<fieldset className={styles.body} disabled={busy || locked}>
					<MondayReportEditor draft={draft} update={update} />
				</fieldset>
			)}

			<footer className={styles.footer}>
				<p>{t`Edits save as you go and do not change your time in ClickUp.`}</p>
				<div className={styles.footerActions}>
					<ActionButton icon={<OutlookLogo size={16} />} disabled={busy || locked} onClick={() => deliver('outlook')}>
						{t`Outlook draft`}
					</ActionButton>
					{preview ? (
						<ActionButton
							tone="primary"
							icon={<IconSend size={16} stroke={1.75} />}
							disabled={busy || locked || !draft.recipients.length}
							onClick={() => deliver('send')}
						>
							{busy ? t`Sending…` : recipientNames ? t`Send to ${recipientNames}` : t`Send report`}
						</ActionButton>
					) : (
						<ActionButton tone="primary" disabled={locked} onClick={() => setPreview(true)}>
							{t`Preview and send`}
						</ActionButton>
					)}
				</div>
			</footer>
		</article>
	);
}
